export type VariantDefinitions = Record<string, Record<string, string>>;

/**
 * Creates a typed utility for composing Tailwind/class strings from variant definitions.
 *
 * @typeParam TVariants - Map of variant names to their possible values and class names.
 * @typeParam TDefaults - Optional map of default variant values.
 *
 * @param config.base - Base class string always included.
 * @param config.variants - Variant definitions: { size: { sm: '...', md: '...' }, ... }.
 * @param config.defaults - Default variant values applied when not overridden.
 *
 * @returns A function that:
 * - Accepts a variant props object and returns the composed class string.
 * - Exposes the original `variants` and `defaults` as static properties.
 */
export function defineVariants<
  TVariants extends VariantDefinitions,
  TDefaults extends Partial<{
    [K in keyof TVariants]: keyof TVariants[K];
  }>
>(config: { base?: string; variants: TVariants; defaults?: TDefaults }) {
  type VariantProps = {
    [K in keyof TVariants]?: keyof TVariants[K];
  };

  return Object.assign(
    (props: VariantProps = {}) => {
      const merged = { ...config.defaults, ...props };
      const classes = [config.base ?? ''];

      for (const key in merged) {
        const value = merged[key];
        if (!value) continue;

        const variantKey = key as keyof TVariants;
        const variantValue = value as keyof TVariants[typeof variantKey];

        const variantClasses = config.variants[variantKey]?.[variantValue];
        if (variantClasses) classes.push(variantClasses);
      }

      return classes.join(' ').trim();
    },
    {
      variants: config.variants,
      defaults: config.defaults
    }
  );
}
